/**
 * Durable trace of an ongoing impersonation.
 *
 * The only in-band signal that a session is impersonated is the `originalAdminId`
 * claim of the access token — and an impersonation access token is short-lived and
 * carries no refresh token of its own. Once it expires its cookie is simply gone,
 * so nothing distinguishes "the admin never impersonated" from "the impersonation
 * just ran out": the next request silently mints a fresh admin access token from
 * the admin refresh token that stayed in place all along, and the admin is dropped
 * back into their own session with no explanation.
 *
 * This marker survives that expiry — and a page reload, which wipes the Redux
 * state — so the sync can tell the two apart and report what happened.
 *
 * sessionStorage, and not localStorage: the notice is only worth giving while the
 * admin is still in the browsing session where they impersonated someone. Told
 * two weeks later, on a fresh visit, it reports an event the admin has long
 * forgotten and hijacks the URL they actually asked for. Per-tab storage expresses
 * that window exactly, and expires the marker on its own — no TTL to maintain.
 */
export const IMPERSONATION_MARKER_STORAGE_KEY = "impersonating";

export const markImpersonationStarted = (): void => {
  if (typeof sessionStorage === "undefined") return;
  sessionStorage.setItem(IMPERSONATION_MARKER_STORAGE_KEY, "true");
};

export const clearImpersonationMarker = (): void => {
  if (typeof sessionStorage === "undefined") return;
  sessionStorage.removeItem(IMPERSONATION_MARKER_STORAGE_KEY);
};

export const wasImpersonating = (): boolean => {
  if (typeof sessionStorage === "undefined") return false;
  return sessionStorage.getItem(IMPERSONATION_MARKER_STORAGE_KEY) === "true";
};
